'use client';

import { useEffect } from 'react';

export interface RecidUrlSyncProps {
  /** The advanced-search session id to reflect in the URL. */
  recid: string;
}

/**
 * Ensures the current URL carries `?recid=<recid>` WITHOUT triggering a
 * navigation.
 *
 * The advanced-search session key (`recid`) keys the localStorage-persisted
 * filter state. It is generated on the server when the URL lacks it, then
 * written back here via `history.replaceState`.
 *
 * Why not a server `redirect()`? A server redirect performs a client-side
 * navigation to a NON-prefetched route. That makes the Next App Router state a
 * pending Promise, which flips `useActionQueue`'s conditional `use()` hook
 * (react-dom app-router) between renders — throwing React #310 ("Rendered more
 * hooks than during the previous render") on the concurrent, data-driven
 * InstantSearch render. `replaceState` only rewrites the address bar: no
 * navigation, no router-state Promise, no hook-count oscillation.
 *
 * All other query params are preserved. Renders nothing.
 */
export function RecidUrlSync({ recid }: RecidUrlSyncProps): null {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('recid') === recid) return;

    params.set('recid', recid);
    const nextUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
    window.history.replaceState(window.history.state, '', nextUrl);
  }, [recid]);

  return null;
}
